import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAppDispatch } from '@/Redux/store';
import { login } from '@/Redux/Slices/authSlice';
import { useAuth } from '@/Hooks/useAuth';
import { AuthShell } from './AuthShell';
import { AuthField } from './AuthField';

export default function Login() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation() as { state?: { from?: string; email?: string } };
  /**
   * Prefilled when `/register` sends someone here — the account was created but
   * the automatic sign-in did not land, and retyping an email they entered a
   * moment ago is the sort of friction that makes a working signup feel broken.
   */
  const [identifier, setIdentifier] = useState(location.state?.email ?? '');
  const [password, setPassword] = useState('');

  if (isAuthenticated) return <Navigate to={location.state?.from ?? '/'} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(login({ identifier, password }));
    if (login.rejected.match(result)) toast.error(String(result.payload));
  };

  return (
    <AuthShell>
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{t('auth.login')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            {/*
              `AuthField` rather than a bare label + `Input`: the label used to be
              a sibling of the input with no `htmlFor`, so clicking the word did
              not focus the field and assistive technology never associated the
              two. See the note in `AuthField` — the shared `components/ui/field`
              still has that shape and is a separate, repo-wide fix.
            */}
            <AuthField
              type="text"
              label={t('auth.email')}
              autoComplete="username"
              dir="ltr"
              autoCapitalize="none"
              spellCheck={false}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
            />

            <AuthField
              type="password"
              label={t('auth.password')}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t('common.loading') : t('auth.login')}
            </Button>
          </form>

          {/*
            The two ways out of this screen, which it did not previously have:
            there was no route to `/register` at all, and nothing anywhere said
            what to do about a forgotten password. `/forgot-password` is not a
            reset form — there is no reset in this product — it is the page that
            says so and names the person who can actually help.
          */}
          <div className="mt-4 space-y-3 border-t border-line pt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-ink-muted underline underline-offset-4 hover:text-ink"
            >
              {t('auth.forgotPassword')}
            </Link>

            <div className="space-y-2">
              <p className="text-sm text-ink-muted">{t('auth.noAccount')}</p>
              <Link
                to="/register"
                className={buttonVariants({ variant: 'outline', className: 'w-full' })}
              >
                {t('auth.signUp')}
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </AuthShell>
  );
}
